import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import classNames from 'classnames';
import get from 'lodash/get';
import { TrackJS } from 'trackjs';

import { ImpressionMetrics } from '../analytics/impression-metrics';
import { WallpaperConfig } from '../config/wallpaper-config';
import { PostMessageService } from '../services/postmessage-service';
import { PreloadService } from '../services/preload-service';
import { WallpaperActions } from '../store/actions';

import WallpaperHTML from './wallpaper-html';
import WallpaperImage from './wallpaper-image';
import WallpaperVideo from './wallpaper-video';

import Router from '@module/router';
import Logger from '@module/logger';

import './stylesheets/wallpaper.scss';

const MAX_TITLE_LENGTH = 150;

export class Wallpaper extends React.PureComponent {
  state = {
    creative: null,
    position: 0,
  };

  componentDidMount() {
    // Start wallpaper rotation!
    this.props.rotateWallpaper();
  }

  componentDidUpdate(prevProps) {
    const { route, wallpaper } = this.props;
    const advertId = get(route, 'params.advert_id');

    // If the user navigates to a wallpaper from the gallery,
    // make sure we show it forcing the rotation
    if (advertId && get(prevProps, 'route.params.advert_id') !== advertId) {
      this.props.rotateWallpaper();
    }

    if (get(prevProps, 'wallpaper.position') !== wallpaper.position) {
      this.preloadCreative();
    }

    if (!wallpaper.isRotationEnabled || wallpaper.isHidden) return;

    // We pause the rotation when the panel is open.
    if (
      prevProps.route.isPanelOpen !== route.isPanelOpen &&
      !wallpaper.isRotationPaused
    ) {
      this.props.pauseRotation();
    }

    // Resume the rotation when the panel is closed
    if (
      prevProps.route.isPanelOpen !== route.isPanelOpen &&
      wallpaper.isRotationPaused
    ) {
      this.props.startOrResumeRotation(wallpaper);
    }
  }

  componentDidCatch(error) {
    // For easier debugging on production, we are logging error messages in the console
    Logger.error(error);
    TrackJS.track(error);
    this.props.showFallbackWallpaper();
  }

  static getDerivedStateFromError() {
    return {
      creative: null,
    };
  }

  async preloadCreative() {
    const { wallpaper } = this.props;

    try {
      PostMessageService.stop();
      await PreloadService.load(wallpaper.creative);
      this.setState({
        creative: wallpaper.creative,
        position: wallpaper.position,
      });

      ImpressionMetrics.sendWallpaperImpressionHits(wallpaper);
    } catch (error) {
      TrackJS.track(error);
      this.props.rotateWallpaper();
    }
  }

  closePanel() {
    Router.navigateTo(Router.getLastRoute('transfer', '/'));
  }

  renderWallpaperLink() {
    if (!this.state.creative.meta) return null;

    const { href, title } = this.state.creative.meta;

    const wallpaperTitle =
      title && title.length > MAX_TITLE_LENGTH
        ? title.slice(0, MAX_TITLE_LENGTH) + '...'
        : title;

    if (href && title) {
      return (
        <a
          href={href}
          rel="noopener noreferrer"
          className="wallpaper__title"
          target="_blank"
          title={title}
        >
          {wallpaperTitle}
        </a>
      );
    }

    if (title) {
      return (
        <span className="wallpaper__title" title={title}>
          {wallpaperTitle}
        </span>
      );
    }

    return null;
  }

  renderBlockingLayer() {
    const { route } = this.props;

    if (!route.isPanelOpen) return null;

    return (
      <div
        className="wallpaper__cover"
        onClick={this.closePanel}
        onKeyPress={this.closePanel}
        role="link"
        tabIndex="0"
      />
    );
  }

  renderWallpaper() {
    const { creative } = this.state;

    switch (creative.format) {
      case WallpaperConfig.format.IMAGE:
        return <WallpaperImage creative={creative} />;
      case WallpaperConfig.format.HTML5:
        return <WallpaperHTML creative={creative} />;
      case WallpaperConfig.format.VIDEO:
        return <WallpaperVideo creative={creative} />;
    }
  }

  render() {
    const { route } = this.props;
    const { creative, position } = this.state;

    const wallpaperClassNames = classNames('wallpaper', {
      'wallpaper--no-transition': !route.isPanelOpen,
      'wallpaper--half-panel': route.isPanelHalfOpen,
      'wallpaper--full-panel': route.isPanelFullOpen,
    });

    return (
      <div className={wallpaperClassNames}>
        {this.renderBlockingLayer()}
        {creative && (
          <TransitionGroup appear={true} component={null}>
            <CSSTransition
              // The key property change will trigger the transition.
              key={`creative.src-${position}`}
              timeout={WallpaperConfig.transition.duration}
              classNames={{
                appear: 'wallpaper__content--appear',
                appearActive: 'wallpaper__content--appear-active',
                enter: 'wallpaper__content--enter',
                enterActive: 'wallpaper__content--enter-active',
                exit: 'wallpaper__content--exit',
                exitActive: 'wallpaper__content--exit-active',
                exitDone: 'wallpaper__content--exit-active',
              }}
            >
              {/* This cannot be React.Fragment, because TransitionGroup expects a "real" DOM element */}
              <div>
                {this.renderWallpaper()}
                {this.renderWallpaperLink()}
              </div>
            </CSSTransition>
          </TransitionGroup>
        )}
      </div>
    );
  }
}

Wallpaper.propTypes = {
  pauseRotation: PropTypes.func.isRequired,
  rotateWallpaper: PropTypes.func.isRequired,
  route: PropTypes.object.isRequired,
  showFallbackWallpaper: PropTypes.func.isRequired,
  startOrResumeRotation: PropTypes.func.isRequired,
  wallpaper: PropTypes.object,
};

Wallpaper.defaultProps = {
  wallpaper: {},
};

const mapStateToProps = ({ route, wallpaper }) => ({
  route,
  wallpaper,
});

const mapDispatchToProps = {
  determineProviderAndRotate: WallpaperActions.determineProviderAndRotate,
  pauseRotation: WallpaperActions.pauseRotation,
  rotateWallpaper: WallpaperActions.rotateWallpaper,
  showFallbackWallpaper: WallpaperActions.showFallbackWallpaper,
  startOrResumeRotation: WallpaperActions.startOrResumeRotation,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Wallpaper);
